import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ScrollIndicator } from "@/overlay/ScrollIndicator";

export function HeroSection() {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const onMove = (e) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty("--mx", `${x * 10}px`);
      el.style.setProperty("--my", `${y * 10}px`);
      if (logoRef.current) {
        logoRef.current.style.setProperty("--lx", `${x * 18}px`);
        logoRef.current.style.setProperty("--ly", `${y * 18}px`);
      }
    };
    el.addEventListener("mousemove", onMove);
    return () => {
      el.removeEventListener("mousemove", onMove);
    };
  }, []);

  const isZh = i18n.language?.startsWith("zh");

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full flex items-center"
    >
      <div className="hero-glow" aria-hidden="true" />

      {/* Floating HTML logo overlay (desktop only - cleaner alpha than WebGL texture) */}
      <div
        ref={logoRef}
        aria-hidden="true"
        className="hidden md:block pointer-events-none absolute z-[6] select-none"
        data-testid="hero-logo-overlay"
        style={{
          right: "8%",
          top: "50%",
          transform: "translate3d(var(--lx,0px), calc(-50% + var(--ly,0px)), 0)",
          transition: "transform 180ms ease-out",
          animation: "floatY 6s ease-in-out infinite, logoPulseDesktop 4.5s ease-in-out infinite",
          width: "clamp(260px, 28vw, 460px)",
          height: "clamp(260px, 28vw, 460px)",
        }}
      >
        <img
          src="/assets/poseidon-logo-v3.png"
          alt="Poseidon Music Logo"
          draggable="false"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>

      <div
        className="relative z-10 w-full max-w-[1120px] mx-auto px-5 sm:px-8"
        style={{
          transform: "translate3d(var(--mx,0px), var(--my,0px), 0)",
          transition: "transform 160ms ease-out",
        }}
      >
        <div className="max-w-[820px]">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-[1px] w-8 bg-[var(--gold-400)]/60" />
            <span className="text-[11px] tracking-[0.3em] uppercase font-mono-brand text-[var(--paper-100)]/80">
              {t("hero.eyebrow")}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={[
              "leading-[1.02] tracking-[-0.02em]",
              "text-5xl sm:text-7xl lg:text-[96px]",
              "text-[var(--paper-50)] text-shadow-depth",
              isZh ? "font-display-cn" : "font-display-en",
            ].join(" ")}
            data-testid="hero-tagline"
          >
            {isZh ? (
              <>
                潮起潮落
                <br className="hidden sm:block" />
                <span className="mr-2 text-[var(--gold-400)] opacity-80">·</span>
                <span className="gold-shimmer-text">乐韵永恒</span>
              </>
            ) : (
              <span>
                Tides Rise and Fall
                <br className="hidden sm:block" />
                <span className="mr-2 text-[var(--gold-400)] opacity-80">·</span>
                <span className="gold-shimmer-text">Music Echoes Eternal</span>
              </span>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={[
              "mt-8 max-w-[560px] text-[var(--paper-100)]/85 text-base sm:text-lg leading-relaxed",
              isZh ? "font-body-cn" : "font-body-en",
            ].join(" ")}
          >
            {isZh ? (
              <>
                Poseidon Music · 海神音乐
                <span className="mx-2 text-[var(--muted-400)]">—</span>
                专注中文音乐发行的全球厂牌。
              </>
            ) : (
              <>
                Poseidon Music · 海神音乐
                <span className="mx-2 text-[var(--muted-400)]">—</span>
                A global label dedicated to Mandarin music releases.
              </>
            )}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.0 }}
            className="mt-14 sm:mt-20 flex items-center gap-8"
          >
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[var(--gold-400)] shadow-[0_0_12px_rgba(240,196,90,0.8)] animate-pulse" />
              <span className="text-[11px] tracking-[0.24em] uppercase font-mono-brand text-[var(--paper-100)]/70">
                {isZh ? "中文首选 · CHINESE FIRST" : "MANDARIN · 华语优先"}
              </span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 1.3 }}
          className="absolute left-1/2 bottom-8 -translate-x-1/2"
        >
          <ScrollIndicator />
        </motion.div>

        {/* Mobile inline logo (shown only when md hidden) */}
        <div
          aria-hidden="true"
          className="md:hidden absolute right-4 -top-8 opacity-30 pointer-events-none"
          style={{ width: "180px", height: "180px" }}
        >
          <img
            src="/assets/poseidon-logo-v3.png"
            alt=""
            draggable="false"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
      </div>
    </section>
  );
}
